/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import type { Key } from 'react';

import { Autocomplete, Button } from '@coursera/cds-core';
import { AddIcon } from '@coursera/cds-icons';

import _t from 'i18n!nls/account-profile';

const styles = {
  headerButton: () => css`
    justify-content: flex-start;
    padding-left: 0;
  `,
  header: () => css`
    padding-right: var(--cds-spacing-300);
  `,
};

type Props = {
  options: { label: string; value: string }[];
  onSelect: (value: string) => void;
  placeholder: string;
  label?: string;
  onSearchChange?: (query: string) => void;
  errorMessage?: string;
  'data-e2e'?: string;
};

const SelectWithCreateNewOption = ({
  options,
  onSelect,
  placeholder,
  label,
  onSearchChange,
  errorMessage,
  'data-e2e': e2e,
}: Props) => {
  const [query, setQuery] = useState<string>('');
  const [defaultValue, setDefaultValue] = useState<string>('');
  const [limited, setLimited] = useState<{ value: string; label: string }[]>([]);

  const createNewOption = () => {
    // Check to see if the option already exists
    const existingOption = options.find((option) => option.label === query);

    if (!existingOption) {
      onSelect(query);
    } else {
      onSelect(existingOption.value);
    }
  };

  const updateLimitedOptions = useCallback<() => void>(() => {
    const filteredOptions = onSearchChange
      ? options
      : options.filter((option) => option.label.toLocaleLowerCase().includes(query.toLocaleLowerCase()));

    if (filteredOptions.length > 100) {
      const first100Options = filteredOptions.slice(0, 100);
      first100Options.concat({ value: 'create-new', label: query });
      first100Options.push({
        value: 'others',
        label: _t('And #{remainingOptions} more...', { remainingOptions: options.length - 100 }),
      });
      setLimited(first100Options);
    } else {
      setLimited(filteredOptions.slice(0, 100));
    }
  }, [options, onSearchChange, query]);

  useEffect(() => {
    updateLimitedOptions();
  }, [updateLimitedOptions]);

  const handleInputChange = (searchQuery: string) => {
    setQuery(searchQuery);
    if (onSearchChange) {
      onSearchChange(searchQuery);
    } else {
      updateLimitedOptions();
    }
  };

  const handleSelectionChange = (key: Key) => {
    if (!key || key === 'others') return;

    if (key === 'create-new') {
      createNewOption();
      setDefaultValue(key.toString());
    } else {
      onSelect(key.toString());
      setDefaultValue(key.toString());
    }
  };

  return (
    <Autocomplete
      aria-label={label ?? placeholder}
      data-e2e={e2e}
      placeholder={placeholder}
      fullWidth
      onInputChange={handleInputChange}
      onSelectionChange={handleSelectionChange}
      popoverTrigger="focus"
      allowsCustomValue
      items={[...(query ? [{ value: 'create-new', label: query }] : []), ...limited]}
      selectedKey={defaultValue}
      renderNotFoundMessage={() => <div>No result. {errorMessage}</div>}
    >
      {(item) => {
        if (item.value === 'create-new') {
          return (
            <Autocomplete.Option
              aria-label={_t('#{query} not found, #{errorMessage}. Create item "#{query}"', { query, errorMessage })}
              textValue={query}
              key="create-new"
            >
              <Button
                onClick={createNewOption}
                css={styles.headerButton}
                variant="ghost"
                iconPosition="before"
                icon={<AddIcon />}
                fullWidth
              >
                {_t('Create item "#{query}"', { query })}
              </Button>
            </Autocomplete.Option>
          );
        }
        return <Autocomplete.Option key={item.value}>{item.label}</Autocomplete.Option>;
      }}
    </Autocomplete>
  );
};

export default SelectWithCreateNewOption;
